import React, { useState, useEffect, useRef } from "react";
import { toastr } from "react-redux-toastr";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { getDataFromAPICreateCombo } from "./action";
import ComboCreateComponent from "./component";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

const ComboCreateContainer = (props) => {
  const navigate = useNavigate();
  const [offset, setOffset] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [retail_price, setretail_price] = useState("");
  const [offer_price, setoffer_price] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [applyOnKiosk, setApplyOnKiosk] = useState(false);
  const [applyOnConsumerApp, setApplyOnConsumerApp] = useState(false);
  const [mvConfig, setMvConfig] = useState([]);
  const [mvIdList, setMvIdList] = useState([]);
  const [mvId, setMvId] = useState("");
  const [selectedMvId, setSelectedMvId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedMvItems, setSelectedMvItems] = useState([]);
  const [errors, setErrors] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [imagePath, setImagePath] = useState(null);
  const [isImageUploaded, setIsImageUploaded] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [price, setPrice] = useState("");
  const [hasMoreData, setHasMoreData] = useState(true);
  const tableContainerRef = useRef(null);
  const [filteredMvList, setFilteredMvList] = useState([]);

  // Debug function to log payload
  const logPayload = (payload) => {
    console.log("========== PAYLOAD BEING SENT ==========");
    console.log("name:", payload.name);
    console.log("description:", payload.description);
    console.log("total_price:", payload.total_price);
    console.log("actual_price:", payload.actual_price);
    console.log("activates_at:", payload.activates_at);
    console.log("expires_at:", payload.expires_at);
    console.log("consumer_app_enabled:", payload.consumer_app_enabled);
    console.log("kiosk_app_enabled:", payload.kiosk_app_enabled);
    console.log("image_url:", payload.image_url);
    console.log("mv_config:", JSON.stringify(payload.mv_config, null, 2));
    console.log("combo_cohorts:", payload.combo_cohorts);
    console.log("========================================");
  };

  // Validate payload before sending
  const validatePayload = (payload) => {
    const issues = [];
    
    if (!payload.name || payload.name.trim() === "") {
      issues.push("Name is required");
    }
    if (!payload.description || payload.description.trim() === "") {
      issues.push("Description is required");
    }
    if (!payload.total_price || isNaN(payload.total_price)) {
      issues.push("Total price (offer_price) is required and must be a number");
    }
    if (!payload.actual_price || isNaN(payload.actual_price)) {
      issues.push("Actual price (retail_price) is required and must be a number");
    }
    if (!payload.activates_at) {
      issues.push("Start date is required");
    }
    if (!payload.expires_at) {
      issues.push("End date is required");
    }
    if (!payload.mv_config || payload.mv_config.length === 0) {
      issues.push("At least one MV item is required");
    }
    
    return issues;
  };

  const filteredMvIdList = Array.isArray(mvIdList)
    ? mvIdList.filter(
        (item) =>
          (item.id &&
            item.id.toString().toLowerCase().includes(searchQuery.toLowerCase())) ||
          (item.variant_name &&
            item.variant_name.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : [];

  const handleShowSuccessModal = () => {
    setSuccessModalOpen(true);
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalOpen(false);
    navigate(-1);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleSelectMvId = (item) => {
    setSelectedMvItems((prevSelected) => {
      if (prevSelected.some((selectedItem) => selectedItem.id === item.id)) {
        return prevSelected;
      }
      return [...prevSelected, { ...item, quantity: 1, price: item.offer_price || item.mrp || 0 }];
    });
    setMvId("");
    handleCloseModal();
  };

  const handleQuantityChange = (id, newQuantity) => {
    setSelectedMvItems((prevSelected) =>
      prevSelected.map((item) =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const incrementQuantity = (id) => {
    setSelectedMvItems((prevSelected) =>
      prevSelected.map((item) =>
        item.id === id ? { ...item, quantity: item.quantity + 1 } : item
      )
    );
  };

  const decrementQuantity = (id) => {
    setSelectedMvItems((prevSelected) => {
      const updatedItems = prevSelected.map((item) =>
        item.id === id
          ? {
              ...item,
              quantity: item.quantity > 0 ? item.quantity - 1 : item.quantity,
            }
          : item
      );
      return updatedItems.filter((item) => item.quantity > 0);
    });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      const allowedExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];
      const fileExtension = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

      if (!allowedTypes.includes(file.type) || !allowedExtensions.includes(fileExtension)) {
        toastr.error("Error", "Only image files are allowed (JPG, PNG, GIF, WEBP)");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
      setSelectedFile(file);
    } else {
      setImagePreview(null);
    }
  };

  const handleImageUpload = () => {
    if (!selectedFile) {
      toastr.error("Error", "Please select an image");
      return;
    }

    const formData = new FormData();
    formData.append("image_file", selectedFile);
    formData.append("image_type", "combo");

    props.getDataFromAPI(
      `/dashboard/api/v2/admin/screensaver/upload`,
      "POST",
      formData,
      (response) => {
        toastr.success("Created", "Image uploaded successfully");
        setFilePreview(null);
        setImagePath(response.screenSaverUrl);
        setIsImageUploaded(true);
      },
      (err) => {
        toastr.error("Failed", "Unable to upload image");
        console.error("Image upload error:", err);
      }
    );
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setImagePreview(null);
  };

  const submitApiButton = () => {
    handleCreateComboApi();
  };

  const validateComboForm = (data) => {
    const errors = {};
    const whitespaceOnlyPattern = /^\s*$/;
    const pricePattern = /^(?!0$)[0-9]{1,6}$/;

    const {
      name,
      description,
      retail_price,
      offer_price,
      startDate,
      endDate,
      applyOnKiosk,
      applyOnConsumerApp,
      selectedMvItems,
    } = data;

    if (!name || whitespaceOnlyPattern.test(name)) {
      errors.name = "Name is required";
    } else if (name.length < 3) {
      errors.name = "Name must be at least 3 characters";
    }

    if (!description || whitespaceOnlyPattern.test(description)) {
      errors.description = "Description is required";
    }

    if (!retail_price || !pricePattern.test(retail_price)) {
      errors.retail_price = "Selling price must be a valid number";
    }

    if (!offer_price || !pricePattern.test(offer_price)) {
      errors.offer_price = "Offer price must be a valid number";
    }

    const currentUtc = dayjs().utc();
    const startDateUtc = startDate ? dayjs(startDate).utc() : null;
    const endDateUtc = endDate ? dayjs(endDate).utc() : null;

    if (!startDate || !startDateUtc) {
      errors.date = "Start date is required";
    } else if (startDateUtc.isBefore(currentUtc)) {
      errors.date = "Start date cannot be in the past";
    } else if (!endDate || !endDateUtc) {
      errors.date = "End date is required";
    } else if (startDateUtc.isAfter(endDateUtc) || startDateUtc.isSame(endDateUtc)) {
      errors.date = "Start date must be before the end date";
    }

    if (!applyOnKiosk && !applyOnConsumerApp) {
      errors.selection = "Select at least one application";
    }

    if (selectedMvItems.length === 0) {
      errors.mvConfig = "Please select at least one MV ID";
    } else {
      const totalMvPrice = selectedMvItems.reduce(
        (sum, item) => sum + (item.price || 0) * (item.quantity || 1),
        0
      );
      if (totalMvPrice !== parseInt(offer_price, 10)) {
        errors.mvConfig = `Total MV price (${totalMvPrice}) must match offer price (${offer_price})`;
      }
    }

    return errors;
  };

  const handleCreateComboApi = async () => {
    const data = {
      name,
      description,
      retail_price,
      offer_price,
      startDate,
      endDate,
      applyOnKiosk,
      applyOnConsumerApp,
      selectedMvItems,
    };

    // Validate form
    const formErrors = validateComboForm(data);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      console.error("Form validation errors:", formErrors);
      return;
    }
    setErrors({});

    // Prepare dates
    const startDateUtc = startDate ? dayjs(startDate).utc().toISOString() : null;
    const endDateUtc = endDate ? dayjs(endDate).utc().toISOString() : null;

    // Prepare MV config
    const mvConfigPayload = selectedMvItems.map((item) => ({
      mv_id: item.id,
      quantity: item.quantity,
      price: parseInt(item.price, 10) || 0,
    }));

    const payload = {
      name: name.trim(),
      description: description.trim(),
      total_price: parseInt(offer_price, 10),
      actual_price: parseInt(retail_price, 10),
      activates_at: startDateUtc,
      expires_at: endDateUtc,
      consumer_app_enabled: applyOnConsumerApp,
      kiosk_app_enabled: applyOnKiosk,
      image_url: imagePath || null,
      mv_config: mvConfigPayload,
      combo_cohorts: [],
    };

    // Debug: Log payload before sending
    logPayload(payload);

    // Validate payload
    const payloadIssues = validatePayload(payload);
    if (payloadIssues.length > 0) {
      console.error("Payload validation failed:", payloadIssues);
      toastr.error("Validation Error", payloadIssues.join(", "));
      return;
    }

    try {
      const response = await props.getDataFromAPICreateCombo(
        `/partner/api/v2/combo/create`,
        "POST",
        payload,
        (response) => {
          console.log("API Success Response:", response);
          if (response && response.status === "200") {
            toastr.success("Success", "Combo created successfully");
            navigate("/home/combolist");
          } else if (response && response.status && response.status.code === "200") {
            toastr.success("Success", "Combo created successfully");
            navigate("/home/combolist");
          } else {
            toastr.success("Success", "Combo created successfully");
            navigate("/home/combolist");
          }
        },
        (err) => {
          console.error("API Error Response:", err);
          
          // Show detailed error message
          let errorMsg = "Failed to create combo";
          if (err?.status?.message) {
            errorMsg = err.status.message;
          } else if (err?.message) {
            errorMsg = err.message;
          } else if (err?.response?.data?.message) {
            errorMsg = err.response.data.message;
          }
          
          toastr.error("Error", errorMsg);
        }
      );
      
      console.log("Response from API:", response);
      
    } catch (err) {
      console.error("Exception in API call:", err);
      toastr.error("Error", err?.message || "Something went wrong");
    }
  };

  useEffect(() => {
    const fetchInitialData = () => {
      props.getDataFromAPI(
        `/dashboard/api/v2/admin/mvs/all?limit=50&isActive=1&offset=0`,
        "GET",
        undefined,
        (response) => {
          const list = response?.list || [];
          setMvIdList(list);
          setFilteredMvList(list);
          setHasMoreData(list.length === 30);
        },
        (err) => {
          console.error("API Error fetching MV list:", err);
        }
      );
    };
    fetchInitialData();
  }, []);

  const handleSearchMvId = debounce((query = "") => {
    if (query.trim() === "") {
      setFilteredMvList(mvIdList);
      return;
    }
    props.getDataFromAPI(
      `/dashboard/api/v2/admin/mvs/all?limit=50&offset=0&isActive=1&search=${query}`,
      "GET",
      undefined,
      (response) => {
        setFilteredMvList(response?.list || []);
      },
      (err) => {
        console.error("API Error:", err);
      }
    );
  }, 300);

  const handleSearchQueryChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    handleSearchMvId(value);
  };

  return (
    <ComboCreateComponent
      isLoading={isLoading}
      name={name}
      description={description}
      retail_price={retail_price}
      offer_price={offer_price}
      startDate={startDate}
      endDate={endDate}
      file={file}
      imagePreview={imagePreview}
      applyOnKiosk={applyOnKiosk}
      applyOnConsumerApp={applyOnConsumerApp}
      mvConfig={mvConfig}
      setName={setName}
      setDescription={setDescription}
      setretail_price={setretail_price}
      setoffer_price={setoffer_price}
      setStartDate={setStartDate}
      setEndDate={setEndDate}
      setFile={setFile}
      setApplyOnKiosk={setApplyOnKiosk}
      setApplyOnConsumerApp={setApplyOnConsumerApp}
      handleQuantityChange={handleQuantityChange}
      handleFileChange={handleFileChange}
      incrementQuantity={incrementQuantity}
      handleRemoveFile={handleRemoveFile}
      handleCreateComboApi={handleCreateComboApi}
      mvIdList={mvIdList}
      setMvId={setMvId}
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      selectedMvId={selectedMvId}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      filteredMvIdList={filteredMvIdList}
      handleOpenModal={handleOpenModal}
      handleSelectMvId={handleSelectMvId}
      handleCloseModal={handleCloseModal}
      decrementQuantity={decrementQuantity}
      setSelectedMvItems={setSelectedMvItems}
      selectedMvItems={selectedMvItems}
      errors={errors}
      successModalOpen={successModalOpen}
      handleCloseSuccessModal={handleCloseSuccessModal}
      handleImageUpload={handleImageUpload}
      selectedFile={selectedFile}
      isImageUploaded={isImageUploaded}
      price={price}
      setPrice={setPrice}
      handleSearchQueryChange={handleSearchQueryChange}
      filteredMvList={filteredMvList}
      submitApiButton={submitApiButton}
      tableContainerRef={tableContainerRef}
      hasMoreData={hasMoreData}
    />
  );
};

function mapStateToProps(props) {
  return {
    props,
  };
}

export default connect(mapStateToProps, {
  getDataFromAPI,
  getDataFromAPICreateCombo,
})(ComboCreateContainer);